import { useQuery } from '@tanstack/react-query';
import { createClient } from '@/lib/supabase/client';
import { queryKeys } from '@/lib/queries/queryKeys';
import { recordServerTime, serverDate } from '@/lib/time/serverClock';

/**
 * The current time on the server's clock, for render-time comparisons with
 * server timestamps (test/survey availability, deadlines). The browser clock
 * may be wrong (Viber in-app WebView, misconfigured phone timezone).
 *
 * Returns serverDate() – device time corrected by the offset from
 * src/lib/time/serverClock.ts, so it is right from the first render (the boot
 * script sets the offset before hydration) and advances on every render
 * instead of freezing at fetch time. The get_server_now() RPC is kept as one
 * more server-time sample. Inside callbacks and effects call serverDate()
 * directly – a value captured from this hook goes stale.
 */
export function useServerNow(): Date {
  const supabase = createClient();

  useQuery<number | null>({
    queryKey: queryKeys.serverNow,
    queryFn: async () => {
      const deviceAtRequest = Date.now();
      const { data, error } = await supabase.rpc('get_server_now');
      if (error || !data) return null;
      // The server stamped `data` somewhere during the round trip; the midpoint
      // is the best device-side estimate of that moment.
      const serverMs = new Date(data).getTime();
      recordServerTime(serverMs, (deviceAtRequest + Date.now()) / 2);
      return serverMs;
    },
    staleTime: 60 * 1000,
  });

  return serverDate();
}
